import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../contexts/AuthContext';

const styles: Record<string, React.CSSProperties> = {
  /* page wrapper */
  page: {
    minHeight: '100vh',
    fontFamily: "'Segoe UI', Arial, sans-serif",
    backgroundColor: '#ffffff',
    color: '#1a1a1a',

    display: 'flex',
    flexDirection: 'column',
  },

  /* ── light-blue hero band ── */
  heroBand: {
    backgroundColor: '#d6e8f5',
    padding: '60px 60px 60px 100px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },

  heroCard: {
    backgroundColor: '#0d2240',
    borderRadius: '16px',
    padding: '45px 52px 51px',
    maxWidth: '540px',
    width: '100%',
    color: '#ffffff',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  },

  heroTitle: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '32px',
    lineHeight: 1.2,
  },

  heroSupportTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '8px',
  },

  heroSupportText: {
    fontFamily: '"IBM Plex Sans", sans-serif',
    fontSize: '13px',
    fontWeight: 400,
    color: 'rgba(255,255,255,0.82)',
    marginBottom: '28px',
    lineHeight: 1.6,
  },

  heroContactBtn: {
    display: 'inline-block',
    padding: '12px 32px',
    backgroundColor: '#ffffff',
    color: '#0d2240',
    border: 'none',
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
  },

  /* ── main content area ── */
  mainContent: {
    padding: '50px 60px 60px 100px',
    maxWidth: '900px',
    flex: 1,
  },

  /* Limited Access section */
  limitedAccessSection: {
    marginBottom: '48px',
  },

  sectionH1: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '14px',
  },

  sectionText: {
    fontFamily: '"IBM Plex Sans", sans-serif',
    fontSize: '13px',
    fontWeight: 400,
    color: '#555',
    marginBottom: '24px',
    lineHeight: 1.6,
  },

  accessBtn: {
    display: 'inline-block',
    padding: '14px 28px',
    backgroundColor: '#0d2240',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
  },

  divider: {
    border: 'none',
    borderTop: '1px solid #e5e7eb',
    margin: '32px 0',
  },

  /* footer */
  footer: {
    backgroundColor: '#071a2a',
    padding: '28px 40px',
    fontFamily: '"IBM Plex Sans", sans-serif',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '12px',
    marginTop: 'auto',
  },

  footerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  footerLink: {
    color: '#ffffff',
    textDecoration: 'none' as const,
    cursor: 'pointer',
  },

  separator: {
    color: '#ffffff',
  },
};

type MainTab = 'merchant' | 'client' | 'subscriptions';
type EnvTab = 'PR' | 'SB';

const Home = () => {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();

  const [activeMain, setActiveMain] = useState<MainTab>('merchant');
  const [activeEnv, setActiveEnv] = useState<EnvTab>('PR');

  const features = [
    {
      title: t('home.clientCredentialsTitle'),
      description: t('home.clientCredentialsDesc'),
      icon: 'bi-key',
      color: 'primary',
      link: ROUTES.CLIENT_CREDENTIALS,
      requiresAuth: true,
    },
  ];

  return (
    <div style={styles.page}>
      {/* ── MAIN CONTENT ── */}
      <div style={styles.mainContent}>
        {/* Limited Access */}
        <div style={styles.limitedAccessSection}>
          <h1 style={styles.sectionH1}>{t('home.limitedAccess')}</h1>

          <p style={styles.sectionText}>
            {t('home.limitedAccessText')}
          </p>

          <button
            onClick={() => login()}
            style={styles.accessBtn}
          >
            {t('home.accessToYourAccount')}
          </button>
        </div>

        <hr style={styles.divider} />
      </div>

      {/* ── FOOTER ── */}
      <footer style={styles.footer}>
        <a href={`https://www.moneris.com${i18n.language === 'fr' ? '/fr' : ''}`} style={styles.footerLink}>
          Moneris.com
        </a>
        <div style={styles.footerRow}>
          <a
            href={`https://api-developer.moneris.com${i18n.language === 'fr' ? '/fr' : ''}/tos`}
            style={styles.footerLink}
          >
            {t('home.termsOfUse', { defaultValue: 'Terms of Use' })}
          </a>
          <span style={styles.separator}>|</span>
          <a
            href={`https://www.moneris.com${i18n.language === 'fr' ? '/fr-ca/juridiques/enonce-sur-la-protection-des-renseignements-personnels' : '/en/legal/privacy-statement'}`}
            style={styles.footerLink}
          >
            {t('home.privacyNotice', { defaultValue: 'Privacy Notice' })}
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Home;